import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post, put, qs } from '../api';
import { alertCopy, lastHappened, type AlertRow } from '../alerts';
import { msg, tr } from '../i18n';
import { useSession, useTenant } from '../session';
import { SharingSettings } from './shares';
import {
  ago, Badge, bytes, Button, Card, ConfirmDialog, CopyBox, Empty, ErrorBox, Field, friendlyError, Loading, Modal, money, PageHead, Progress, Segmented, StateBadge,
  TechDetails, useStepUp, useToast, when,
} from '../ui';

// ───────── team ─────────

const roleName = (role: string) => (role === 'admin' ? tr('Admin') : tr('View only'));
const roleDesc = (role: string) =>
  role === 'admin'
    ? tr('Can change everything: computers, backup plans, getting files back, people and the subscription.')
    : tr('Can see how backups are doing and which files are backed up, but cannot change anything or get files back.');

export function Users() {
  const t = useTenant();
  const qc = useQueryClient();
  const toast = useToast();
  const { me } = useSession();
  const q = useQuery({ queryKey: ['members', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}/members`) });
  const [invite, setInvite] = useState(false);
  const [removing, setRemoving] = useState<{ id: string; email: string } | null>(null);
  const manage = t.can('users.manage');
  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast(ok);
      qc.invalidateQueries({ queryKey: ['members'] });
    } catch (e) {
      toast(friendlyError(e), true);
    }
  };
  return (
    <div>
      <PageHead title={tr('Team')} desc={tr('People who can sign in to ZeroGap for your organization.')} actions={manage ? <Button variant="primary" onClick={() => setInvite(true)}>{tr('Invite someone')}</Button> : undefined} />
      <div className="card table-wrap">
        {q.isLoading ? <div className="card-pad"><Loading /></div> : q.error ? <div className="card-pad"><ErrorBox error={q.error} /></div> : (
          <table>
            <thead><tr><th>{tr('Person')}</th><th>{tr('Can do')}</th><th>{tr('Two-step sign-in')}</th><th>{tr('Joined')}</th><th /></tr></thead>
            <tbody>
              {q.data.members.map((m: any) => (
                <tr key={m.user_id}>
                  <td><strong>{m.name || m.email}</strong><div className="small muted mono">{m.email}</div></td>
                  <td>
                    {manage && m.user_id !== me?.user.id ? (
                      <select value={m.role} style={{ width: 150 }} onChange={(e) => act(() => patch(`/api/t/${t.tenantId}/members/${m.user_id}`, { role: e.target.value }), tr('Saved'))}>
                        <option value="admin">{tr('Admin')}</option><option value="viewer">{tr('View only')}</option>
                      </select>
                    ) : <Badge tone={m.role === 'admin' ? 'brass' : 'neutral'} dot={false}>{roleName(m.role)}</Badge>}
                  </td>
                  <td>{m.mfa_enabled ? <Badge tone="ok">{tr('On')}</Badge> : <Badge tone="warn">{tr('Off')}</Badge>}</td>
                  <td className="small">{when(m.created_at)}</td>
                  <td className="right">{manage && m.user_id !== me?.user.id && <Button size="sm" variant="ghost" onClick={() => setRemoving({ id: m.user_id, email: m.email })}>{tr('Remove')}</Button>}</td>
                </tr>
              ))}
              {q.data.invitations.map((i: any) => (
                <tr key={i.id}>
                  <td><span className="muted mono">{i.email}</span><div className="small muted">{tr('Invited {sent} · the invitation expires {expires}', { sent: ago(i.created_at), expires: ago(i.expires_at) })}</div></td>
                  <td><Badge tone="info">{tr('Invited as {role}', { role: roleName(i.role) })}</Badge></td>
                  <td /><td />
                  <td className="right">{manage && <Button size="sm" variant="ghost" onClick={() => act(() => del(`/api/t/${t.tenantId}/members/invitations/${i.id}`), tr('Invitation canceled'))}>{tr('Cancel invitation')}</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="grid g2" style={{ marginTop: 16 }}>
        <div className="small"><strong>{tr('Admin')}:</strong> {roleDesc('admin')}</div>
        <div className="small"><strong>{tr('View only')}:</strong> {roleDesc('viewer')}</div>
      </div>
      {invite && <InviteDialog onClose={() => setInvite(false)} onDone={() => { setInvite(false); qc.invalidateQueries({ queryKey: ['members'] }); toast(tr('Invitation sent')); }} />}
      {removing && (
        <ConfirmDialog title={tr('Remove {email}?', { email: removing.email })} confirm={tr('Remove')} danger onClose={() => setRemoving(null)}
          onConfirm={async () => { await del(`/api/t/${t.tenantId}/members/${removing.id}`); setRemoving(null); qc.invalidateQueries({ queryKey: ['members'] }); toast(tr('Removed from the organization')); }}>
          <p style={{ margin: 0 }}>{tr('They will no longer be able to sign in to this organization. Nothing else changes.')}</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function InviteDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const t = useTenant();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'viewer' | 'admin'>('viewer');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={tr('Invite someone')} onClose={onClose} footer={<><Button onClick={onClose}>{tr('Cancel')}</Button><Button variant="primary" busy={busy} onClick={async () => { setBusy(true); try { await post(`/api/t/${t.tenantId}/members/invitations`, { email, role }); onDone(); } catch (e) { setErr(friendlyError(e)); } finally { setBusy(false); } }}>{tr('Send invitation')}</Button></>}>
      <Field label={tr('Email')}><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus /></Field>
      <Field label={tr('What can they do?')} hint={roleDesc(role)}><Segmented value={role} onChange={setRole} options={[{ key: 'viewer', label: tr('View only') }, { key: 'admin', label: tr('Admin') }]} /></Field>
      {err && <div className="notice bad">{err}</div>}
    </Modal>
  );
}

// ───────── activity ─────────

const ACTIVITY: Record<string, string> = {
  'auth.login': msg('signed in'),
  'auth.logout': msg('signed out'),
  'auth.password_changed': msg('changed their password'),
  'auth.password_reset': msg('reset their password'),
  'auth.mfa_enabled': msg('turned on two-step sign-in'),
  'auth.mfa_disabled': msg('turned off two-step sign-in'),
  'auth.email_verified': msg('confirmed their email address'),
  'authz.denied': msg('tried something they are not allowed to do'),
  'backup.requested': msg('started a backup by hand'),
  'backup.cancel_requested': msg('stopped a backup'),
  'backup.committed': msg('finished a backup'),
  'device.enrolled': msg('connected a new computer'),
  'device.activated': msg('computer is ready'),
  'device.updated': msg('changed a computer'),
  'device.bulk_updated': msg('changed several computers'),
  'device.quarantined': msg('blocked a computer'),
  'device.retired': msg('removed a computer'),
  'device.credentials_rotated': msg('replaced a computer’s storage key'),
  'device.credentials_rotation_requested': msg('asked for a new storage key'),
  'enrollment_token.created': msg('created a connection code'),
  'enrollment_token.revoked': msg('canceled a connection code'),
  'policy.created': msg('created a backup plan'),
  'policy.updated': msg('changed a backup plan'),
  'policy.assigned': msg('moved computers to a backup plan'),
  'policy.set_default': msg('chose the default backup plan'),
  'policy.paused': msg('paused backups for a plan'),
  'policy.resumed': msg('resumed backups for a plan'),
  'policy.archived': msg('deleted a backup plan'),
  'restore.requested': msg('started getting files back'),
  'restore.downloaded': msg('downloaded restored files'),
  'restore.cancel_requested': msg('canceled getting files back'),
  'restore.retried': msg('retried getting files back'),
  'restore.completed': msg('finished getting files back'),
  'share.created': msg('made a shared link'),
  'share.updated': msg('changed a shared link'),
  'share.link_copied': msg('copied a shared link'),
  'share.stopped': msg('stopped a shared link'),
  'share.ended': msg('a shared link ended'),
  'share.opened': msg('opened a shared link'),
  'share.password_failed': msg('typed a wrong password for a shared link'),
  'share.locked': msg('a shared link was locked after too many wrong passwords'),
  'share.denied': msg('tried a shared link that no longer works'),
  'share.file_downloaded': msg('downloaded a file from a shared link'),
  'share.zip_downloaded': msg('downloaded files from a shared link'),
  'member.invited': msg('invited someone'),
  'member.joined': msg('joined the organization'),
  'member.removed': msg('removed someone'),
  'member.role_changed': msg('changed what someone can do'),
  'member.invitation_revoked': msg('canceled an invitation'),
  'tenant.created': msg('created the organization'),
  'tenant.settings_updated': msg('changed the organization settings'),
  'tenant.deletion_requested': msg('asked to delete the organization'),
  'tenant.deletion_canceled': msg('canceled deleting the organization'),
  'webhook.created': msg('connected another tool'),
  'webhook.deleted': msg('disconnected a tool'),
  'support.session_started': msg('ZeroGap support started helping'),
  'support.session_ended': msg('ZeroGap support finished helping'),
  'retention.expired': msg('old backups were removed as planned'),
  'storage.provisioned': msg('backup storage is ready'),
};

export function Audit() {
  const t = useTenant();
  const [action, setAction] = useState('');
  const [before, setBefore] = useState<number[]>([]);
  const cur = before.at(-1);
  const q = useQuery({ queryKey: ['audit', t.tenantId, action, cur], queryFn: () => get(`/api/t/${t.tenantId}/audit${qs({ action, before: cur, limit: 100 })}`) });
  const who = (e: any) =>
    e.actor_type === 'operator' ? tr('ZeroGap support ({email})', { email: e.operator_email }) :
    e.actor_type === 'device' ? tr('Computer {name}', { name: e.device_name ?? '' }) :
    e.actor_type === 'system' ? tr('ZeroGap') :
    e.actor_type === 'anonymous' && e.action.startsWith('share.') ? tr('Someone with the link') :
    e.actor_email ?? tr('Someone not signed in');
  return (
    <div>
      <PageHead title={tr('Activity')} desc={tr('Everything that happened in your organization: who signed in, what changed, and who got files back. This record cannot be edited.')} />
      <div className="row" style={{ marginBottom: 12 }}>
        <select value={action} onChange={(e) => { setAction(e.target.value); setBefore([]); }} style={{ width: 260 }} aria-label={tr('Show')}>
          <option value="">{tr('Everything')}</option><option value="restore">{tr('Getting files back')}</option><option value="device">{tr('Computers')}</option><option value="policy">{tr('Backup plans')}</option>
          <option value="member">{tr('People')}</option><option value="auth">{tr('Sign-ins and security')}</option><option value="support">{tr('ZeroGap support')}</option><option value="authz.denied">{tr('Blocked attempts')}</option><option value="backup">{tr('Backups')}</option>
          <option value="share">{tr('Shared links')}</option>
        </select>
      </div>
      <div className="card table-wrap">
        {q.isLoading ? <div className="card-pad"><Loading /></div> : q.error ? <div className="card-pad"><ErrorBox error={q.error} /></div> : (
          <table>
            <thead><tr><th>{tr('When')}</th><th>{tr('Who')}</th><th>{tr('What happened')}</th><th /></tr></thead>
            <tbody>
              {q.data.events.map((e: any) => (
                <tr key={e.id}>
                  <td className="small" style={{ whiteSpace: 'nowrap', width: 170 }}>{when(e.occurred_at)}</td>
                  <td className="small"><span className={e.actor_type === 'operator' ? 'chip' : ''}>{who(e)}</span></td>
                  <td>
                    <span className="cap">{ACTIVITY[e.action] ? tr(ACTIVITY[e.action]!) : <span className="mono small">{e.action}</span>}</span>
                    {e.details?.name && <> · <strong>{e.details.name}</strong></>}
                    {e.outcome === 'denied' && <> <Badge tone="bad">{tr('blocked')}</Badge></>}
                    {e.outcome === 'failure' && <> <Badge tone="warn">{tr('failed')}</Badge></>}
                    {e.affected_email && <div className="small muted">{tr('Affected: {email}', { email: e.affected_email })}</div>}
                  </td>
                  <td style={{ width: 120 }}>
                    {Object.keys(e.details ?? {}).length > 0 && <TechDetails inline summary={tr('Details')}><pre className="codebox" style={{ maxWidth: 420 }}>{JSON.stringify(e.details, null, 2)}</pre></TechDetails>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
        <Button size="sm" disabled={!before.length} onClick={() => setBefore(before.slice(0, -1))}>{tr('Newer')}</Button>
        <Button size="sm" disabled={!q.data?.nextBefore} onClick={() => setBefore([...before, q.data.nextBefore])}>{tr('Older')}</Button>
      </div>
    </div>
  );
}

// ───────── alerts & messages ─────────

const MESSAGE_KIND: Record<string, string> = {
  backup_failed: msg("A backup didn't finish"),
  backup_missed: msg('A computer has not been backed up for a while'),
  device_offline: msg('A computer has not connected for a long time'),
  quota_threshold: msg('Your storage is almost full'),
  suspicious_change: msg('Unusual changes on a computer'),
  verification_failed: msg('An automatic check failed'),
  restore_completed: msg('Your files are back'),
  restore_failed: msg("Getting files back didn't fully work"),
  backup_failed_resolved: msg('Solved: backups are working again'),
  backup_missed_resolved: msg('Solved: backups are working again'),
  device_offline_resolved: msg('Solved: the computer is connected again'),
  verification_failed_resolved: msg('Solved: the automatic check passed'),
};

export function Alerts() {
  const t = useTenant();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const tab = (sp.get('tab') as 'open' | 'resolved' | 'messages' | null) ?? 'open';
  const q = useQuery({ queryKey: ['alerts', t.tenantId, 'all'], queryFn: () => get<{ alerts: AlertRow[] }>(`/api/t/${t.tenantId}/alerts?state=all`) });
  const notif = useQuery({ queryKey: ['notif', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}/notifications`), enabled: !t.support });
  const ten = useQuery({ queryKey: ['tenant', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}`) });
  const paused = ten.data?.tenant?.cleanup_paused_until && new Date(ten.data.tenant.cleanup_paused_until) > new Date();
  const all = q.data?.alerts ?? [];
  const shown = all.filter((a) => (tab === 'resolved' ? a.state === 'resolved' : a.state !== 'resolved'));
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['alerts'] });
    qc.invalidateQueries({ queryKey: ['alerts-count'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  };
  return (
    <div>
      <PageHead title={tr('Alerts')} desc={tr('Problems that need your attention. Most of them clear up on their own, and you get an email when something new happens.')} />
      {paused && (
        <div className="notice warn" style={{ marginBottom: 16 }}>
          <strong>{tr('Old backups are being kept longer, until {time}', { time: when(ten.data.tenant.cleanup_paused_until) })}</strong>
          {tr('Because of unusual changes, nothing is removed from your history while you check.')}
          {t.can('retention.manage') && <div style={{ marginTop: 8 }}><Button size="sm" onClick={async () => { await post(`/api/t/${t.tenantId}/cleanup-pause`, { days: 0 }); qc.invalidateQueries({ queryKey: ['tenant'] }); }}>{tr('Everything is fine, go back to normal')}</Button></div>}
        </div>
      )}
      <div style={{ marginBottom: 12 }}>
        <Segmented value={tab} onChange={(v) => setSp(v === 'open' ? {} : { tab: v })} options={[
          { key: 'open', label: tr('Needs attention') },
          { key: 'resolved', label: tr('Handled') },
          ...(t.support ? [] : [{ key: 'messages' as const, label: (notif.data?.unread ?? 0) > 0 ? tr('Messages ({n})', { n: notif.data.unread }) : tr('Messages') }]),
        ]} />
      </div>
      {tab === 'messages' ? (
        <Messages data={notif.data} loading={notif.isLoading} onRead={async () => { await post(`/api/t/${t.tenantId}/notifications/read`, {}); qc.invalidateQueries({ queryKey: ['notif'] }); }} />
      ) : (
        <div className="card table-wrap">
          {q.isLoading ? <div className="card-pad"><Loading /></div> : q.error ? <div className="card-pad"><ErrorBox error={q.error} /></div> : shown.length === 0 ? (
            <Empty glyph="✓" title={tab === 'open' ? tr('Nothing needs your attention') : tr('Nothing here yet')} />
          ) : (
            <table>
              <tbody>
                {shown.map((a) => {
                  const c = alertCopy(a, t.base);
                  return (
                    <tr key={a.id}>
                      <td style={{ width: 130 }}><Badge tone={a.state === 'resolved' ? 'ok' : c.tone}>{a.state === 'resolved' ? tr('Handled') : c.level}</Badge></td>
                      <td>
                        <strong>{c.title}</strong>
                        <div className="small muted">{c.todo}</div>
                        {a.state === 'acknowledged' && <div className="small">{tr('Someone is already looking at this.')}</div>}
                        <TechDetails inline summary={tr('Details')}><span className="small" dir="ltr">{a.title}: {a.message}</span></TechDetails>
                      </td>
                      <td className="small" style={{ whiteSpace: 'nowrap' }}>{lastHappened(a)}</td>
                      <td className="right" style={{ whiteSpace: 'nowrap' }}>
                        {c.link && <Link className="btn sm" to={c.link.to}>{c.link.label}</Link>}{' '}
                        {t.can('alerts.manage') && a.state !== 'resolved' && <Button size="sm" variant="ghost" onClick={async () => { await post(`/api/t/${t.tenantId}/alerts/${a.id}/resolve`); refresh(); }}>{tr('Mark as handled')}</Button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function Messages({ data, loading, onRead }: { data: any; loading: boolean; onRead: () => void }) {
  return (
    <div className="card table-wrap">
      {loading ? <div className="card-pad"><Loading /></div> : !data?.notifications?.length ? <Empty glyph="✉" title={tr('No messages')} /> : (
        <>
          <div className="card-head"><span className="small muted">{tr('Messages sent to you, newest first.')}</span><Button size="sm" onClick={onRead}>{tr('Mark all as read')}</Button></div>
          <table>
            <tbody>
              {data.notifications.map((n: any) => (
                <tr key={n.id} style={{ fontWeight: n.read_at ? 400 : 600 }}>
                  <td>
                    <div>{MESSAGE_KIND[n.kind] ? tr(MESSAGE_KIND[n.kind]!) : n.title}</div>
                    <TechDetails inline summary={tr('Details')}><span className="small" dir="ltr" style={{ fontWeight: 400 }}>{n.title}: {n.body}</span></TechDetails>
                  </td>
                  <td className="small muted" style={{ whiteSpace: 'nowrap' }}>{ago(n.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

// ───────── subscription & usage ─────────

export function Billing() {
  const t = useTenant();
  const toast = useToast();
  const manage = t.can('settings.manage');
  const usage = useQuery({ queryKey: ['usage', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}/usage`) });
  const billing = useQuery({ queryKey: ['billing', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}/billing`), enabled: t.can('billing.read') });
  const assistantUsage = useQuery({ queryKey: ['assistant-usage', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}/assistant/usage`), enabled: manage });
  const hist = usage.data?.history ?? [];
  const max = Math.max(1, ...hist.map((h: any) => Number(h.physical_bytes)));
  const limits = (x: { device_limit: number; seat_limit: number; storage_gb: number }) => tr('Up to {devices} computers, {seats} people and {gb} GB of storage', { devices: x.device_limit, seats: x.seat_limit, gb: x.storage_gb });
  const sub = billing.data?.subscription;
  return (
    <div>
      <PageHead title={tr('Subscription & usage')} actions={<a className="btn" href={`/api/t/${t.tenantId}/usage/export.csv`}>{tr('Download usage (CSV)')}</a>} />
      {billing.data && (
        <div className="grid g2" style={{ marginBottom: 16 }}>
          <div className="card stat">
            <div className="label">{tr('Your subscription')}</div>
            <div className="value" style={{ fontSize: 26 }}>{sub?.plan_name ? tr(sub.plan_name) : '—'}</div>
            <div className="foot">
              {sub && limits(sub)}
              {sub?.status === 'trialing' && sub.trial_ends_at && <div>{tr('Free trial, ends {time}', { time: ago(sub.trial_ends_at) })}</div>}
            </div>
          </div>
          <div className="card stat">
            <div className="label">{tr('How you pay')}</div>
            <div className="foot" style={{ fontSize: 14, marginTop: 8 }}>{billing.data.manualMode ? tr('By invoice from your account manager. Nothing is charged automatically.') : tr('By card, every month.')}</div>
          </div>
        </div>
      )}
      {sub && ['past_due', 'unpaid'].includes(sub.status) && <div className="notice warn" style={{ marginBottom: 16 }}><strong>{tr('A payment is needed')}</strong>{tr('Backups continue until {time}. Your existing backups are never deleted because of a missed payment.', { time: when(sub.grace_until) })}</div>}
      {manage && assistantUsage.data && (
        <Card title={tr('Assistant usage (this month)')}>
          <div className="small muted" style={{ marginBottom: 6 }}>{tr('{used} / {allowed} turns', { used: assistantUsage.data.turnsUsed, allowed: assistantUsage.data.turnsAllowed })}</div>
          <Progress value={assistantUsage.data.turnsAllowed > 0 ? (100 * assistantUsage.data.turnsUsed) / assistantUsage.data.turnsAllowed : 0} />
          {assistantUsage.data.byUser.length === 0 ? <Empty glyph="·" title={tr('No assistant turns this month')} /> : (
            <table style={{ marginTop: 14 }}><thead><tr><th>{tr('Person')}</th><th className="right">{tr('Turns')}</th></tr></thead>
              <tbody>{assistantUsage.data.byUser.map((u: any) => <tr key={u.user_id}><td className="mono small">{u.email}</td><td className="right num">{u.turns}</td></tr>)}</tbody></table>
          )}
        </Card>
      )}
      <Card title={tr('Storage used over the last 90 days')}>
        {hist.length === 0 ? <Empty glyph="∿" title={tr('Nothing to show yet')} /> : (
          <svg viewBox={`0 0 ${hist.length * 8} 120`} style={{ width: '100%', height: 140 }} role="img" aria-label={tr('Storage used over time')} preserveAspectRatio="none">
            {[...hist].reverse().map((h: any, i: number) => {
              const hgt = (110 * Number(h.physical_bytes)) / max;
              return <rect key={h.day} x={i * 8 + 1} y={115 - hgt} width={6} height={hgt} fill="var(--brass)" rx="1"><title>{String(h.day).slice(0, 10)}: {bytes(h.physical_bytes)}</title></rect>;
            })}
          </svg>
        )}
        <p className="small muted" style={{ marginBottom: 0 }}>{tr('Storage used is smaller than the size of your files, because files that repeat are stored once and everything is compressed.')}</p>
      </Card>
      <Card title={tr('By computer')} pad={false}>
        <table>
          <thead><tr><th>{tr('Computer')}</th><th className="right">{tr('Size of files')}</th><th className="right">{tr('Storage used')}</th><th className="right">{tr('Saved backups')}</th></tr></thead>
          <tbody>{usage.data?.perDevice.map((d: any) => <tr key={d.id}><td>{d.display_name}</td><td className="right num">{bytes(d.storage_logical_bytes)}</td><td className="right num">{bytes(d.storage_physical_bytes)}</td><td className="right num">{d.recovery_points}</td></tr>)}</tbody>
        </table>
      </Card>
      {billing.data && (
        <>
          <h2 style={{ margin: '22px 0 12px' }}>{tr('Subscriptions')}</h2>
          <div className="grid g3">
            {billing.data.plans.map((p: any) => (
              <div key={p.key} className="card card-pad stack" style={{ gap: 6 }}>
                <h3>{tr(p.name)}</h3>
                <div className="num" style={{ fontSize: 22 }}>{money(p.price_cents, p.currency)}<span className="muted small"> {tr('a month, before VAT')}</span></div>
                <div className="small muted">{limits(p)}</div>
                {t.can('billing.manage') && (
                  sub?.plan_key === p.key ? <Badge tone="ok">{tr('Your current subscription')}</Badge> : (
                    <Button onClick={async () => { try { const r = await post(`/api/t/${t.tenantId}/billing/checkout`, { planKey: p.key }); if (r.url) window.location.href = r.url; else toast(r.message); } catch (e) { toast(friendlyError(e), true); } }}>{tr('Switch to this')}</Button>
                  )
                )}
              </div>
            ))}
          </div>
          <Card title={tr('Invoices')} pad={false}>
            {billing.data.invoices.length === 0 ? <Empty glyph="§" title={tr('No invoices yet')} /> : (
              <table><tbody>{billing.data.invoices.map((i: any) => <tr key={i.id}><td className="mono">{i.number ?? i.id.slice(0, 8)}</td><td className="small">{when(i.issued_at)}</td><td className="num">{money(i.amount_cents, i.currency)}</td><td>{i.status === 'paid' ? <Badge tone="ok">{tr('Paid')}</Badge> : <StateBadge state={i.status} />}</td><td>{i.hosted_url && <a href={i.hosted_url} target="_blank" rel="noreferrer noopener">{tr('View')}</a>}</td></tr>)}</tbody></table>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

// ───────── settings ─────────

const zones = (() => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return ['UTC'];
  }
})();

export function Settings() {
  const t = useTenant();
  const { me } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const stepUp = useStepUp(!!me?.user.mfaEnabled);
  const manage = t.can('settings.manage');
  const q = useQuery({ queryKey: ['tenant', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}`) });
  const [f, setF] = useState<any>(null);
  const [confirmName, setConfirmName] = useState('');
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const ten = q.data.tenant;
  const v = f ?? { name: ten.name, timezone: ten.timezone, mfaRequiredForAdmins: !!ten.settings?.mfaRequiredForAdmins, assistantEnabled: !!ten.settings?.assistantEnabled };
  const tzs = zones.includes(v.timezone) ? zones : [v.timezone, ...zones];
  return (
    <div>
      <PageHead title={tr('Settings')} />
      <div className="grid g2">
        <Card title={tr('Organization')}>
          <fieldset disabled={!manage} className="plain-fieldset">
            <Field label={tr('Organization name')}><input type="text" value={v.name} onChange={(e) => setF({ ...v, name: e.target.value })} /></Field>
            <Field label={tr('Time zone')} hint={tr('Used for reports and emails.')}>
              <select value={v.timezone} onChange={(e) => setF({ ...v, timezone: e.target.value })}>{tzs.map((z) => <option key={z} value={z}>{z}</option>)}</select>
            </Field>
            <label className="check" style={{ marginBottom: 14 }}>
              <input type="checkbox" checked={v.mfaRequiredForAdmins} onChange={(e) => setF({ ...v, mfaRequiredForAdmins: e.target.checked })} />
              <span>{tr('Admins must use two-step sign-in (a code from a phone app)')}</span>
            </label>
            {/* Off until an organization chooses it: the assistant sends file and folder names outward. */}
            <label className="check" style={{ marginBottom: 14 }}>
              <input type="checkbox" checked={v.assistantEnabled} onChange={(e) => setF({ ...v, assistantEnabled: e.target.checked })} />
              <span>
                {tr('Let people ask ZeroGap questions in their own words')}
                <span className="hint" style={{ display: 'block' }}>
                  {tr('Adds a helper that answers questions about your backups and can offer to start a backup or get a file back. It is shown the names of your files and folders — never what is inside them — and nothing happens until the person confirms it.')}
                </span>
              </span>
            </label>
            <Button variant="primary" disabled={!f} onClick={async () => { try { await patch(`/api/t/${t.tenantId}/settings`, v); toast(tr('Saved')); setF(null); qc.invalidateQueries({ queryKey: ['tenant'] }); qc.invalidateQueries({ queryKey: ['me'] }); } catch (e) { toast(friendlyError(e), true); } }}>{tr('Save')}</Button>
          </fieldset>
        </Card>
        <Card title={tr('How your backups are protected')}>
          <ul className="small" style={{ margin: 0, paddingInlineStart: 18, lineHeight: 1.8 }}>
            <li>{tr('Files are encrypted on each computer before they are sent.')}</li>
            <li>{ten.object_lock_mode === 'none' ? tr('Deletion protection is off.') : tr('Backups cannot be deleted or changed for {days} days after they are made, even by someone who has your password.', { days: ten.object_lock_days })}</li>
            <li>{tr('Each computer can reach only its own backups.')}</li>
            <li>{tr('If a computer is lost, ZeroGap can still restore its files for you.')}</li>
          </ul>
          <TechDetails>
            <dl className="kv">
              <dt>{tr('Storage provider')}</dt><dd>{ten.storage_provider} · {ten.storage_region}</dd>
              <dt>{tr('Bucket')}</dt><dd className="mono small">{ten.bucket}</dd>
              <dt>{tr('State')}</dt><dd><StateBadge state={ten.storage_state} /></dd>
              <dt>Object Lock</dt><dd>{ten.object_lock_mode === 'none' ? tr('Off') : `${ten.object_lock_mode}, ${ten.object_lock_days}`}</dd>
            </dl>
            <p className="small muted">{tr('ZeroGap keeps recovery keys, protected by a key-management service, so it can restore files when a computer is lost. This means the service is not "zero-knowledge".')}</p>
          </TechDetails>
        </Card>
      </div>
      {t.can('shares.create') || manage ? <SharingSettings settings={ten.settings} manage={manage} /> : null}
      {!t.support && <EmailPrefs />}
      {t.can('notifications.manage') && <Integrations />}
      {manage && (
        <Card title={tr('Delete the organization')}>
          {ten.status === 'pending_deletion' ? (
            <div className="stack">
              <div className="notice bad"><strong>{tr('The organization will be deleted on {time}', { time: when(ten.purge_after) })}</strong>{tr('Until then everything keeps working and you can still change your mind.')}</div>
              <div><Button onClick={async () => { await del(`/api/t/${t.tenantId}/deletion`); qc.invalidateQueries({ queryKey: ['tenant'] }); toast(tr('Deletion canceled')); }}>{tr('Keep the organization')}</Button></div>
            </div>
          ) : (
            <>
              <p className="muted" style={{ marginTop: 0 }}>{tr('Deletes all computers, backups and settings after 30 days. Until then you can change your mind. To confirm, type the organization name.')}</p>
              <div className="row"><input type="text" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={ten.name} style={{ maxWidth: 320 }} />
                <Button variant="danger solid" disabled={confirmName !== ten.name} onClick={() => stepUp.run(async () => { await post(`/api/t/${t.tenantId}/deletion`, { confirmName }); qc.invalidateQueries({ queryKey: ['tenant'] }); toast(tr('The organization will be deleted in 30 days')); }).catch((e) => toast(friendlyError(e), true))}>{tr('Delete the organization')}</Button>
              </div>
            </>
          )}
        </Card>
      )}
      {stepUp.dialog}
    </div>
  );
}

function EmailPrefs() {
  const t = useTenant();
  const toast = useToast();
  const q = useQuery({ queryKey: ['notif', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}/notifications`) });
  const [prefs, setPrefs] = useState<any>(null);
  const p = prefs ?? q.data?.prefs;
  if (!p) return null;
  const TYPES: [string, string][] = [
    ['backup_failed', tr("A backup didn't finish")],
    ['backup_missed', tr('A computer has not been backed up for a while')],
    ['device_offline', tr('A computer has not connected for a long time')],
    ['quota_threshold', tr('Storage is almost full')],
    ['suspicious_change', tr('Unusual changes on a computer')],
    ['verification_failed', tr('An automatic check failed')],
    ['restore_completed', tr('Getting files back finished')],
    ['agent_update', tr('The ZeroGap app was updated')],
  ];
  return (
    <Card title={tr('Emails I get')}>
      <label className="check" style={{ marginBottom: 12 }}><input type="checkbox" checked={p.email_enabled} onChange={(e) => setPrefs({ ...p, email_enabled: e.target.checked })} /><span>{tr('Email me when something needs attention')}</span></label>
      <fieldset disabled={!p.email_enabled} className="plain-fieldset">
        <div className="check-grid" style={{ marginBottom: 14 }}>
          {TYPES.map(([k, label]) => (
            <label key={k} className="check"><input type="checkbox" checked={!p.muted_types.includes(k)} onChange={(e) => setPrefs({ ...p, muted_types: e.target.checked ? p.muted_types.filter((x: string) => x !== k) : [...p.muted_types, k] })} /><span>{label}</span></label>
          ))}
        </div>
      </fieldset>
      <Field label={tr('Summary email')}><Segmented value={p.digest} onChange={(v) => setPrefs({ ...p, digest: v })} options={[{ key: 'none', label: tr('None') }, { key: 'daily', label: tr('Every day') }, { key: 'weekly', label: tr('Every week') }]} /></Field>
      <Button variant="primary" disabled={!prefs} onClick={async () => { await put(`/api/t/${t.tenantId}/notifications/prefs`, { emailEnabled: p.email_enabled, digest: p.digest, mutedTypes: p.muted_types }); setPrefs(null); toast(tr('Saved')); }}>{tr('Save')}</Button>
      <p className="small muted" style={{ marginBottom: 0 }}>{tr('These settings are only for you. Everyone in the team chooses their own.')}</p>
    </Card>
  );
}

/** Webhooks, for IT staff who connect ZeroGap to other tools. */
function Integrations() {
  const t = useTenant();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['webhooks', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}/webhooks`) });
  const [adding, setAdding] = useState(false);
  const [created, setCreated] = useState<{ secret: string } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const deliveries = useQuery({ queryKey: ['deliveries', open], queryFn: () => get(`/api/t/${t.tenantId}/webhooks/${open}/deliveries`), enabled: !!open });
  return (
    <Card title={tr('Connect other tools (for IT staff)')}>
      <TechDetails summary={tr('Webhooks: send events to other systems')} open={!!q.data?.endpoints?.length}>
        <p className="small" style={{ marginTop: 0 }}>
          {tr('Signed JSON events for tools such as n8n. Each request carries')} <code dir="ltr">ZeroGap-Signature: t=&lt;unix&gt;,v1=HMAC-SHA256(secret, t + '.' + body)</code>. {tr('Payloads never contain credentials or file contents.')}
        </p>
        {created && <div className="notice ok" style={{ marginBottom: 12 }}><strong>{tr('Copy the signing secret now. It is not shown again.')}</strong><CopyBox text={created.secret} /></div>}
        {q.data?.endpoints?.length ? (
          <table>
            <tbody>{q.data.endpoints.map((e: any) => (
              <tr key={e.id}>
                <td className="small"><span className="mono" dir="ltr">{e.url}</span><div className="muted">{e.description}</div><div className="mono muted" dir="ltr">{e.events.join(', ')}</div></td>
                <td>{e.enabled ? (e.consecutive_failures ? <Badge tone="warn">{tr('{n} failures', { n: e.consecutive_failures })}</Badge> : <Badge tone="ok">{tr('Working')}</Badge>) : <Badge tone="bad">{tr('Turned off')}</Badge>}</td>
                <td className="right" style={{ whiteSpace: 'nowrap' }}>
                  <Button size="sm" onClick={() => setOpen(open === e.id ? null : e.id)}>{tr('Deliveries')}</Button>{' '}
                  <Button size="sm" onClick={async () => { await post(`/api/t/${t.tenantId}/webhooks/${e.id}/test`); toast(tr('Test event sent')); }}>{tr('Send test')}</Button>{' '}
                  <Button size="sm" variant="danger" onClick={async () => { await del(`/api/t/${t.tenantId}/webhooks/${e.id}`); qc.invalidateQueries({ queryKey: ['webhooks'] }); }}>{tr('Delete')}</Button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        ) : null}
        {open && (
          <table><tbody>{deliveries.data?.deliveries.map((d: any) => (
            <tr key={d.id}><td className="small">{when(d.created_at)}</td><td className="mono small">{d.event_type}</td><td><StateBadge state={d.state} /></td><td className="small">{d.last_status ?? ''} {d.last_error ?? ''}</td></tr>
          ))}</tbody></table>
        )}
        <Button size="sm" style={{ marginTop: 10 }} onClick={() => setAdding(true)}>{tr('Add endpoint')}</Button>
      </TechDetails>
      {adding && <AddWebhook events={q.data?.events ?? []} onClose={() => setAdding(false)} onDone={(r) => { setAdding(false); setCreated(r); qc.invalidateQueries({ queryKey: ['webhooks'] }); }} />}
    </Card>
  );
}

function AddWebhook({ events, onClose, onDone }: { events: string[]; onClose: () => void; onDone: (r: { secret: string }) => void }) {
  const t = useTenant();
  const [url, setUrl] = useState('https://');
  const [description, setDescription] = useState('');
  const [sel, setSel] = useState<string[]>(['backup.failed', 'backup.missed', 'restore.completed', 'restore.failed']);
  const [err, setErr] = useState<string | null>(null);
  return (
    <Modal title={tr('Add webhook endpoint')} onClose={onClose} footer={<><Button onClick={onClose}>{tr('Cancel')}</Button><Button variant="primary" onClick={async () => { try { onDone(await post(`/api/t/${t.tenantId}/webhooks`, { url, description, events: sel })); } catch (e) { setErr(friendlyError(e)); } }}>{tr('Add')}</Button></>}>
      <Field label={tr('HTTPS URL')} hint={tr('Private, loopback, and cloud-metadata addresses are refused.')}><input type="url" value={url} onChange={(e) => setUrl(e.target.value)} /></Field>
      <Field label={tr('Description')}><input type="text" value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
      <Field label={tr('Events')}>{events.map((e) => <label key={e} className="check"><input type="checkbox" checked={sel.includes(e)} onChange={(x) => setSel(x.target.checked ? [...sel, e] : sel.filter((y) => y !== e))} /><span className="mono small">{e}</span></label>)}</Field>
      {err && <div className="notice bad">{err}</div>}
    </Modal>
  );
}
